import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Transition } from "@headlessui/react";

import useTheme from "hooks/useTheme";
import { buildingComponent } from "actions/loaders";

import AlgoExplorerIcon from "theme/icons/AlgoExplorer";
import SendIcon from "theme/icons/Send";
import NFTExplorerIcon from "theme/icons/NFTExplorer";
import ASANotVerifiedIcon from "theme/icons/ASANotVerified";
import ASARenownedIcon from "theme/icons/ASARenowned";
import ASANotableIcon from "theme/icons/ASANotable";
import ASAVerifiedStandardIcon from "theme/icons/ASAVerifiedStandard";
import ASAFlaggedIcon from "theme/icons/ASAFlagged";
import ViewListIcon from "theme/icons/ViewList";
import ViewTilesIcon from "theme/icons/ViewTiles";
import NftsLoader from "theme/loaders/dashboard/NftsLoader";
import StyledTooltipWithoutArrow from "theme/tooltips/StyledTooltipWithoutArrow";

const Nfts = () => {
	const dispatch = useDispatch();
	const { appLanguage, themeColor } = useTheme();

	const accountInfo = useSelector((state) => state.AccountInfo);
	const fetchingDataFromApi = useSelector((state) => state.FetchingDataFromApi);
	const buildingComponentLoader = useSelector((state) => state.BuildingComponent);

	const [nftViewType, setNftViewType] = useState("list");
	const [nfts, setNfts] = useState(null);

	useEffect(() => {
		if (accountInfo?.assets) {
			setNfts(null);
			generateAssetListWithInfoByAccountAssets(accountInfo.assets);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accountInfo]);

	const generateAssetListWithInfoByAccountAssets = async (assets) => {
		dispatch(buildingComponent(true));

		let nftsArray = [];

		for (let asset of assets) {
			if (asset["is-nft"] === true && asset.deleted === false) nftsArray.push(asset);
		}

		nftsArray = [...nftsArray].sort((a, b) => b.amount - a.amount);

		setNfts(nftsArray);

		dispatch(buildingComponent(false));
	};

	return (
		<Transition
			as={Fragment}
			appear={true}
			show={true}
			enter="transition-opacity duration-500"
			enterFrom="opacity-0"
			enterTo="opacity-100"
		>
			<div className="flex flex-col w-full">
				{fetchingDataFromApi || buildingComponent ? (
					<NftsLoader />
				) : nfts?.length ? (
					<div className="flex flex-col w-full space-y-4">
						<div className="flex sticky top-0 w-full justify-between bg-white">
							<div className="flex space-x-1">
								<p className={`text-md ${themeColor?.text}`}>{appLanguage?.qty}:</p>
								<p className={`text-md ${themeColor?.text}`}>{nfts.length}</p>
							</div>
							<div className="flex px-5">
								{nftViewType === "list" ? (
									<ViewTilesIcon
										className="w-6 cursor-pointer hover:shadow-lg"
										fill={themeColor?.hex}
										onClick={() => setNftViewType("grid")}
									/>
								) : (
									<ViewListIcon
										className="w-6 cursor-pointer hover:shadow-lg"
										fill={themeColor?.hex}
										onClick={() => setNftViewType("list")}
									/>
								)}
							</div>
						</div>

						{nftViewType === "list" ? (
							<div className="flex flex-col w-full items-center justify-center space-y-3">
								{nfts.map((nft, index) => (
									<div
										state={{ assetID: nft["asset-id"] }}
										className="flex w-full items-center justify-between"
										key={index}
									>
										<Link
											to="/NFTInfo"
											state={{ assetID: nft["asset-id"], assetInfo: nft }}
											className="flex w-full items-center space-x-8"
										>
											<div className="rounded-full w-8">
												<img
													className={`w-8 ${themeColor?.text}`}
													alt="asset"
													loading="lazy"
													src={`${process.env.REACT_APP_DAFFI_MAINNET_URL}/assets/image?assetID=${nft["asset-id"]}&width=30`}
												/>
											</div>
											<div className="flex items-center space-x-2">
												<div className="flex flex-col items-end">
													{nft?.verification?.reputation ? (
														nft.verification.reputation === "Verified" ? (
															<ASARenownedIcon className="w-4 h-5" fill={themeColor?.hex} />
														) : nft.verification.reputation === "Notable" ? (
															<ASANotableIcon className="w-4 h-5" fill={themeColor?.hex} />
														) : nft.verification.reputation === "Neutral" ? (
															<ASAVerifiedStandardIcon className="w-4 h-5" fill={themeColor?.hex} />
														) : nft.verification.reputation === "Unsafe" ? (
															<ASAFlaggedIcon className="w-4 h-5" fill={themeColor?.hex} />
														) : nft.verification.reputation === "Not Verified" ? (
															<ASANotVerifiedIcon className="w-4 h-5" fill={themeColor?.hex} />
														) : (
															nft.verification.reputation === "" && (
																<ASANotVerifiedIcon className="w-4 h-5" fill={themeColor?.hex} />
															)
														)
													) : (
														<ASANotVerifiedIcon className="w-5" fill={themeColor?.hex} />
													)}

													<ASANotVerifiedIcon className="w-5" fill={themeColor?.hex} />
												</div>
												<div className="flex-col flex">
													<p className={`text-sm ${themeColor?.text}`}>
														{nft["unit-name"] ? nft["unit-name"] : nft.name ? nft.name : "Unnamed"}
													</p>
													<p className={`text-xs font-bold ${themeColor?.text}`}>{nft.name}</p>
												</div>
											</div>
										</Link>

										<div className="flex w-fit items-center justify-end">
											<div className="flex w-fit items-center justify-end space-x-3 px-5">
												<StyledTooltipWithoutArrow
													title={appLanguage?.nftExplorer}
													children={
														<NFTExplorerIcon
															className={`w-4 cursor-pointer`}
															fill={themeColor?.hex}
															onClick={() =>
																window.open(
																	`https://www.nftexplorer.app/asset/${nft["asset-id"]}`,
																	"_blank"
																)
															}
														/>
													}
												/>
												<StyledTooltipWithoutArrow
													title={appLanguage?.algoExplorer}
													children={
														<AlgoExplorerIcon
															className={`w-4 cursor-pointer`}
															fill={themeColor?.hex}
															onClick={() =>
																window.open(
																	nft["asset-id"] === 0
																		? "https://algoexplorer.io"
																		: `https://algoexplorer.io/asset/${nft["asset-id"]}`,
																	"_blank"
																)
															}
														/>
													}
												/>

												<StyledTooltipWithoutArrow
													title={appLanguage?.send}
													children={
														<Link to="/Send" state={{ asset: nft }} className="flex space-x-2">
															<SendIcon className="w-4" fill={themeColor?.hex} />
															<p className={`text-lg ${themeColor?.text}`}>{nft.amount}</p>
														</Link>
													}
												/>
											</div>
										</div>
									</div>
								))}
							</div>
						) : (
							nftViewType === "grid" && (
								<div className="grid grid-cols-4 px-5 gap-x-5 gap-y-8">
									{nfts.map((nft, index) => (
										<Link
											to="/NFTInfo"
											state={{ assetID: nft["asset-id"], assetInfo: nft }}
											className="flex flex-col w-full items-center justify-between space-y-2"
											key={index}
										>
											<div className="flex-grow flex-col w-full items-end justify-end flex">
												<img
													className={`w-full ${themeColor?.text}`}
													alt="asset"
													loading="lazy"
													src={`${process.env.REACT_APP_DAFFI_MAINNET_URL}/assets/image?assetID=${nft["asset-id"]}&width=150`}
												/>
											</div>

											<div className="flex flex-col w-full items-center space-y-2">
												<div className="flex items-center space-x-8">
													<div className="flex-col flex items-center justify-center">
														<p className={`text-sm ${themeColor?.text}`}>
															{nft["unit-name"]
																? nft["unit-name"]
																: nft.name
																? nft.name
																: "Unnamed"}
														</p>
														<p className={`text-xs font-bold ${themeColor?.text}`}>{nft.name}</p>
													</div>
												</div>

												<div className="flex w-fit items-center justify-end">
													<div className="flex w-fit items-center justify-end space-x-2 px-5">
														<StyledTooltipWithoutArrow
															title={appLanguage?.nftExplorer}
															children={
																<NFTExplorerIcon
																	className={`w-3 cursor-pointer`}
																	fill={themeColor?.hex}
																	onClick={() =>
																		window.open(
																			`https://www.nftexplorer.app/asset/${nft["asset-id"]}`,
																			"_blank"
																		)
																	}
																/>
															}
														/>
														<StyledTooltipWithoutArrow
															title={appLanguage?.algoExplorer}
															children={
																<AlgoExplorerIcon
																	className={`w-3 cursor-pointer`}
																	fill={themeColor?.hex}
																	onClick={() =>
																		window.open(
																			nft["asset-id"] === 0
																				? "https://algoexplorer.io"
																				: `https://algoexplorer.io/asset/${nft["asset-id"]}`,
																			"_blank"
																		)
																	}
																/>
															}
														/>
														<StyledTooltipWithoutArrow
															title={appLanguage?.send}
															children={
																<Link
																	to="/Send"
																	state={{ asset: nft }}
																	className="flex space-x-2"
																>
																	<SendIcon className="w-3" fill={themeColor?.hex} />
																	<p className={`text-lg ${themeColor?.text}`}>{nft.amount}</p>
																</Link>
															}
														/>
													</div>
												</div>
											</div>
										</Link>
									))}
								</div>
							)
						)}
					</div>
				) : (
					<p className={`text-md ${themeColor?.text}`}>{appLanguage?.noNFTFound}</p>
				)}
			</div>
		</Transition>
	);
};

export default Nfts;
